/**
 * A gate's conditions in words. A decided gate's card on a run's canvas
 * (Feature 77 D24): one numbered line per condition, a list test with how
 * many items matched of how many, and a count with what it needed, so a
 * blocked gate says which count fell short. The agent records counts for
 * list tests only, so a rule on one value shows its words alone. And the
 * editor's hover card on a gate: each condition in full, its field named as
 * the inspector names it (`first › result[0].need.evidence`, so two fields
 * that end alike read apart), a list test with its list, the item
 * conditions it reads in parentheses, and the count it needs.
 */
import type {
  LinkageCountOperator,
  LinkageItemRules,
  LinkageListCount,
  LinkageListTest,
  LinkageRule,
  LinkageRules,
} from '@optum/api-client/types';

import { LIST_OPERATOR_LABELS, isListTest } from './lists.js';
import { OPERATOR_LABELS, VALUELESS } from './operators.js';
import { readablePath, shortRef } from './references.js';
import { showValue } from './ValueInput.js';

/** One line of the card, and whether its list test did not hold. */
export interface GateLine {
  text: string;
  short: boolean;
}

const COUNT_SIGNS: Record<LinkageCountOperator, string> = {
  equals: '=',
  not_equals: '≠',
  greater_than: '>',
  greater_or_equal: '≥',
  less_than: '<',
  less_or_equal: '≤',
};

/** A reference's own field name: `success`, `result[0].budget` read as `budget`. */
function fieldName(ref: string): string {
  const parts = ref.split('.');
  const within = parts[0] === 'input' ? parts.slice(1) : parts.slice(3);
  return readablePath(within).at(-1) ?? (parts[0] === 'input' ? 'input' : 'the whole result');
}

/** A rule's words with its field as given: `success is true`, `first › input.mode equals chat`. */
function ruleWords(rule: LinkageRule, field: string): string {
  const value = VALUELESS.has(rule.op) ? '' : ` ${showValue(rule.value)}`;
  return `${field} ${OPERATOR_LABELS[rule.op]}${value}`;
}

/** An item condition's words: `need.evidence is not empty`, `the item equals 3`. */
function itemWords(rule: LinkageRule): string {
  const within = readablePath(rule.ref.split('.').slice(1)).join('.') || 'the item';
  const value = VALUELESS.has(rule.op) ? '' : ` ${showValue(rule.value)}`;
  return `${within} ${OPERATOR_LABELS[rule.op]}${value}`;
}

/** The item conditions of a list test or a list filter, joined as they combine. */
export function itemConditionWords(where: LinkageItemRules): string {
  return where.items.map(itemWords).join(where.join === 'all' ? ' and ' : ' or ');
}

/**
 * A list test in full, its list first and its item conditions in parentheses
 * so a count after them never reads as part of a condition:
 * `first › result: count of items where (need.evidence is not empty) > 1`.
 */
function listTestWords(test: LinkageListTest): string {
  const items = itemConditionWords(test.where);
  const count =
    test.op === 'count_where' && test.count
      ? // The sign and its number stay on one line when the card wraps.
        ` ${COUNT_SIGNS[test.count.op]}\u00a0${test.count.value}`
      : '';
  return `${shortRef(test.ref)}: ${LIST_OPERATOR_LABELS[test.op]}${items ? ` (${items})` : ''}${count}`;
}

/** Each condition in full: its field as the inspector names it, a list test with its list. */
export function conditionWords(rules: LinkageRules): string[] {
  return rules.items.map((rule) =>
    isListTest(rule) ? listTestWords(rule) : ruleWords(rule, shortRef(rule.ref)),
  );
}

/** Each condition in full, numbered as the gate's conditions are. */
export function conditionLines(rules: LinkageRules): string[] {
  return conditionWords(rules).map((words, i) => `${i + 1} ${words}`);
}

/** Whether a list test held, from what the agent counted. */
export function listTestHeld(test: LinkageListTest, count: LinkageListCount): boolean {
  if (count.length === null) return false;
  if (test.op === 'any_where') return count.matched > 0;
  if (test.op === 'every_where') return count.matched === count.length;
  if (!test.count) return false;
  const { op, value } = test.count;
  const matched = count.matched;
  if (op === 'equals') return matched === value;
  if (op === 'not_equals') return matched !== value;
  if (op === 'greater_than') return matched > value;
  if (op === 'greater_or_equal') return matched >= value;
  if (op === 'less_than') return matched < value;
  return matched <= value;
}

/** The card's lines, numbered as the gate's conditions are. */
export function gateLines(rules: LinkageRules, counts: readonly LinkageListCount[]): GateLine[] {
  return rules.items.map((rule, i) => {
    const n = i + 1;
    // The run's marker keeps the short words: it sits between two columns of nodes.
    if (!isListTest(rule))
      return { text: `${n} ${ruleWords(rule, fieldName(rule.ref))}`, short: false };
    const label = LIST_OPERATOR_LABELS[rule.op];
    const count = counts.find((c) => c.rule === i);
    if (count === undefined) return { text: `${n} ${label}`, short: false };
    if (count.length === null) return { text: `${n} ${label}: not a list`, short: true };
    const needs =
      rule.op === 'count_where' && rule.count
        ? `, needs ${COUNT_SIGNS[rule.count.op]} ${rule.count.value}`
        : '';
    return {
      text: `${n} ${label}: ${count.matched} of ${count.length}${needs}`,
      short: !listTestHeld(rule, count),
    };
  });
}
